import { useMemo, useRef, useState } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useParams } from 'react-router-dom';

import { OpenInFull } from '@mui/icons-material';
import {
  Box,
  Collapse,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import * as Ably from 'ably';
import { useChannel } from 'ably/react';

import { CfsApi } from '@/api';
import { AIIcon } from '@/assets/images';
import { AdditionalInfoForm, FormCard } from '@/components';
import { useCFSContext } from '@/hooks';
import { IAdditionalInfo } from '@/models';
import { isCFSViewOnly } from '@/services';
import { useToastStore, useUserStore } from '@/store';
import { openNewWindow, reconcileStarEditDuplicates } from '@/utils';

import { FormItemRoot, LiveInformationRoot } from '../styles';

interface LiveInformationProps {
  isNewWindow?: boolean;
  cfsId?: string;
  isFirstResponderView?: boolean;
  isMaxHeight?: boolean;
  onNewMessages?: (newMessages: number) => void;
  disableSendMessage?: boolean;
  isFirstResponderModal?: boolean;
  maxChars?: number;
}

export const LiveInformation = ({
  isNewWindow,
  isFirstResponderView = false,
  disableSendMessage,
  isFirstResponderModal = false,
  maxChars,
  isMaxHeight = false,
  ...rest
}: LiveInformationProps) => {
  const urlParams = useParams();
  const cfsId = rest.cfsId || String(urlParams.cfsId);

  const { updateToast } = useToastStore();
  const { user, account } = useUserStore();
  const readOnly = isCFSViewOnly(user);
  const enabledAiGeneratedSummary =
    account?.settings?.features?.isEnabledAIGeneratedCFSDescription;

  const theme = useTheme();
  const { cfs, updateCFS } = useCFSContext();
  const [collapse, setCollapse] = useState<boolean>(false);
  const [aiGeneratedSummary, setAiGeneratedSummary] = useState<string>('');

  const editInProgressRef = useRef(false);

  const additionalInfo = useMemo(
    () => reconcileStarEditDuplicates(cfs?.additionalInfo || []),
    [cfs?.additionalInfo],
  );

  const { channel: infoChannel } = useChannel(
    `account:${account?._id}:cfs:${cfsId}`,
    (message: Ably.Types.Message) => {
      if (!cfs || !message?.name || !message.data) return;

      if (message.name === 'UPDATED_ADDITIONAL_INFO') {
        if (editInProgressRef.current) return;

        const updatedItems = message.data.new?.additionalInfo;
        if (!Array.isArray(updatedItems)) return;

        updateCFS({
          ...cfs,
          additionalInfo: updatedItems,
        });
      }
    },
  );

  const { channel: deletionChannel } = useChannel(
    `account:${account?._id}:cfs:${cfsId}`,
    'additionalInfo:deleted',
    (message: Ably.Types.Message) => {
      if (cfs && message?.data?.length) {
        updateCFS({
          ...cfs,
          additionalInfo: message?.data,
        });
      }
    },
  );

  const onSubmit = (
    newDescription: string,
    isEditing?: boolean,
    editingItem?: IAdditionalInfo | null,
  ) => {
    const trimmedNewDesc = newDescription.trim();

    if (!trimmedNewDesc) {
      return;
    }

    if (isEditing && editingItem && trimmedNewDesc) {
      const updatedAdditionalInfo = additionalInfo.map((ad) => {
        const adCreatedById =
          typeof ad.createdBy === 'string' ? ad.createdBy : ad.createdBy._id;
        const editingCreatedById =
          typeof editingItem.createdBy === 'string'
            ? editingItem.createdBy
            : editingItem.createdBy._id;

        if (
          ad.description === editingItem.description &&
          ad.createdAt === editingItem.createdAt &&
          adCreatedById === editingCreatedById
        ) {
          return {
            ...ad,
            description: trimmedNewDesc,
            isEdited: true,
          };
        }
        return ad;
      });

      editInProgressRef.current = true;

      CfsApi.update(cfsId, { additionalInfo: updatedAdditionalInfo } as any)
        .then(() => {
          setTimeout(() => {
            editInProgressRef.current = false;
          }, 2000);
        })
        .catch((err: any) => {
          editInProgressRef.current = false;
          if (cfs) {
            updateCFS({
              ...cfs,
              additionalInfo: cfs.additionalInfo,
            } as any);
          }
          updateToast({ open: true, message: err.message });
        });

      if (cfs) {
        updateCFS({
          ...cfs,
          additionalInfo: updatedAdditionalInfo,
        } as any);
      }

      return;
    }

    const apiPayload = {
      description: trimmedNewDesc,
      createdAt: new Date().toISOString(),
      createdBy: user?._id,
    };

    CfsApi.addAdditionalInfo(cfsId, apiPayload as any)
      .then(() => {
        if (user) {
          const newAdditionalInfo = {
            createdBy: {
              roles: user.roles,
              _id: user._id,
              firstName: user.firstName,
              lastName: user.lastName,
              fullName: user.fullName,
            },
            description: trimmedNewDesc,
            createdAt: new Date().toISOString(),
          };

          if (cfs) {
            updateCFS({
              ...cfs,
              additionalInfo: [...additionalInfo, newAdditionalInfo],
            });
          }
        }
      })
      .catch((err: any) => {
        updateToast({ open: true, message: err.message });
      });
  };

  const generateAiSummary = async () => {
    if (collapse) {
      setCollapse(false);
      return;
    }

    const apiResult = await CfsApi.generateAISummary(cfsId);
    if (apiResult.status === 200) {
      setAiGeneratedSummary(apiResult?.data?.shortDescription);
    }
    setCollapse(!collapse);
  };

  const updateAdditionalInfos = (additionalInfos: IAdditionalInfo[]) => {
    if (!cfs) return;
    if (!additionalInfo?.length) return;

    updateCFS({
      ...cfs,
      additionalInfo: additionalInfos,
    });

    deletionChannel?.publish('additionalInfo:deleted', additionalInfos);
    infoChannel?.publish('additionalInfo:updated', additionalInfos);
  };

  return (
    <LiveInformationRoot>
      {isNewWindow && (
        <HelmetProvider>
          <Helmet>
            <title>{`${cfs?.number} - Live information`}</title>
          </Helmet>
        </HelmetProvider>
      )}
      <FormCard
        title={
          !isFirstResponderView
            ? (isNewWindow && cfs?.number ? `${cfs.number} - ` : '') +
              'Live information'
            : undefined
        }
        sx={{
          '& .cardTitle': {
            padding: '10px 15px',
          },
          '& .divider': {
            marginTop: 0,
          },
        }}
        classes={{
          cardContainer: 'live-info-card-container',
          cardContent: 'live-info-card-content',
        }}
      >
        <FormItemRoot>
          {!isNewWindow && !isFirstResponderView && !readOnly && (
            <>
              {enabledAiGeneratedSummary && (
                <Tooltip title="AI Summary">
                  <IconButton
                    sx={{ position: 'absolute', right: 40, top: 8 }}
                    onClick={() => {
                      generateAiSummary();
                    }}
                    aria-label="AI Summary"
                  >
                    <AIIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Open in New Window">
                <IconButton
                  sx={{ position: 'absolute', right: 10, top: 8 }}
                  onClick={() => {
                    openNewWindow(
                      `/cfs/${cfsId}/live-info`,
                      `${cfsId} - Live information`,
                      500,
                      500,
                    );
                  }}
                  aria-label="Open in New Window"
                >
                  <OpenInFull fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
          <Collapse in={collapse} timeout="auto" unmountOnExit>
            <Box
              sx={{
                position: 'absolute',
                top: 60,
                left: 0,
                right: 0,
                borderRadius: 1,
                boxShadow: 0,
                mb: 2,
                width: '100%',
                backgroundColor: theme.palette.background.paper,
                zIndex: 1,
              }}
            >
              <Divider></Divider>
              <Typography
                sx={{
                  marginTop: '0.5rem',
                  marginBottom: '0.5rem',
                  paddingLeft: '0.8rem',
                  color: 'text.secondary',
                }}
                variant="body2"
              >
                {aiGeneratedSummary}
              </Typography>
              <Divider />
            </Box>
          </Collapse>
          <AdditionalInfoForm
            cfsId={cfsId}
            additionalInfoList={additionalInfo}
            classes={
              isNewWindow
                ? { additionalInfoList: 'new-window' }
                : isFirstResponderView
                ? { additionalInfoList: 'first-responder' }
                : undefined
            }
            onSubmit={onSubmit}
            setNewAdditionalInfo={updateAdditionalInfos}
            isFirstResponderView={isFirstResponderView}
            disableSendMessage={disableSendMessage}
            isFirstResponderModal={isFirstResponderModal}
            maxChars={maxChars}
            mirrorOffsetX={-2}
            isMaxHeight={isMaxHeight}
            readOnly={readOnly}
          />
        </FormItemRoot>
      </FormCard>
    </LiveInformationRoot>
  );
};
